import { useState, useCallback } from 'react'
import type { Book, SortConfig } from '@/types'

/**
 * Pagination info
 */
interface PaginationInfo {
    totalPages: number
    startIndex: number
    endIndex: number
    currentPage: number
    itemsPerPage: number
}

/**
 * Return type for useBookPagination hook
 */
interface UseBookPaginationReturn {
    // State
    currentPage: number
    itemsPerPage: number
    sortConfig: SortConfig

    // Functions
    changePage: (page: number) => void
    changeItemsPerPage: (count: number) => void
    resetPage: () => void
    toggleSort: (key: keyof Book) => void
    sortItems: (items: Book[]) => Book[]
    paginate: (items: Book[]) => Book[]
    getPaginationInfo: (totalItems: number) => PaginationInfo
}

/**
 * Custom hook for managing pagination and sorting
 */
export function useBookPagination(): UseBookPaginationReturn {
    const [currentPage, setCurrentPage] = useState(1)
    const [itemsPerPage, setItemsPerPage] = useState(25)
    const [sortConfig, setSortConfig] = useState<SortConfig>({ key: 'title', direction: 'asc' })

    // Change page
    const changePage = useCallback((page: number) => {
        setCurrentPage(page)
    }, [])

    // Change items per page
    const changeItemsPerPage = useCallback((count: number) => {
        setItemsPerPage(count)
        setCurrentPage(1) // Reset to first page
    }, [])

    // Reset to first page (useful when filters change)
    const resetPage = useCallback(() => {
        setCurrentPage(1)
    }, [])

    // Toggle sort for a column
    const toggleSort = useCallback((key: keyof Book) => {
        setSortConfig((prev) => ({
            key,
            direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc',
        }))
    }, [])

    // Sort items
    const sortItems = useCallback(
        (items: Book[]): Book[] => {
            if (!sortConfig.key) return items

            const sorted = [...items].sort((a, b) => {
                const aVal = a[sortConfig.key]
                const bVal = b[sortConfig.key]

                // Handle arrays (author, categories)
                const aStr = Array.isArray(aVal) ? aVal.join(', ') : String(aVal || '')
                const bStr = Array.isArray(bVal) ? bVal.join(', ') : String(bVal || '')

                if (aStr < bStr) return sortConfig.direction === 'asc' ? -1 : 1
                if (aStr > bStr) return sortConfig.direction === 'asc' ? 1 : -1
                return 0
            })

            return sorted
        },
        [sortConfig]
    )

    // Paginate items
    const paginate = useCallback(
        (items: Book[]): Book[] => {
            const startIndex = (currentPage - 1) * itemsPerPage
            const endIndex = startIndex + itemsPerPage
            return items.slice(startIndex, endIndex)
        },
        [currentPage, itemsPerPage]
    )

    // Get pagination info
    const getPaginationInfo = useCallback(
        (totalItems: number): PaginationInfo => {
            const totalPages = Math.ceil(totalItems / itemsPerPage)
            const startIndex = (currentPage - 1) * itemsPerPage + 1
            const endIndex = Math.min(currentPage * itemsPerPage, totalItems)

            return {
                totalPages,
                startIndex,
                endIndex,
                currentPage,
                itemsPerPage,
            }
        },
        [currentPage, itemsPerPage]
    )

    return {
        // State
        currentPage,
        itemsPerPage,
        sortConfig,

        // Functions
        changePage,
        changeItemsPerPage,
        resetPage,
        toggleSort,
        sortItems,
        paginate,
        getPaginationInfo,
    }
}
